import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import * as Switch from '@radix-ui/react-switch';
import * as Tooltip from '@radix-ui/react-tooltip';
import { Module } from '../types/types';

/*
 * Component representing a module card in the settings page.
 * Displays module information, a toggle to enable/disable,
 * and a button to access specific module settings.
 */
interface Props {
	module: Module;
	onToggle: (id: string) => void;
	configurable: boolean;
}

/*
 * ModuleCard component
 * @param {module} - Module information to display
 * @param {onToggle} - Function to handle module enable/disable
 */
const ModuleCard: React.FC<Props> = ({ module, onToggle, configurable }) => {
	// React Router navigation hook
	const navigate = useNavigate();

	const handleSettingsClick = () => {
		// Navigate to the corresponding page if it exists
		// Assume the route is defined as /app/<module.name>
		navigate(`/app/settings/${module.name}`);
	};

	return (
		<div
			className={
				'border rounded-lg shadow flex hover:shadow-md transition-shadow ' +
				(module.enabled ? '' : 'opacity-50')
			}
		>
			{/* Logo in left side */}
			<div className="w-1/4 bg-gray-100 flex items-center justify-center p-4 rounded-l-lg">
				<FontAwesomeIcon
					icon={module.icon}
					className="w-10 h-10 text-blue-500"
				/>
			</div>

			{/* Infos in right side */}
			<div className="w-3/4 bg-white p-4 flex flex-col justify-between rounded-r-lg">
				<div>
					{/* Title and description */}
					<h3 className="text-lg font-medium">{module.title}</h3>
					<p className="text-gray-500 text-sm mt-1">{module.description}</p>
				</div>
				<div className="mt-4 flex items-center justify-between">
					{/* Toggle with Tooltip Radix */}
					<Tooltip.Provider>
						<Tooltip.Root>
							{module.required && (
								<Tooltip.Trigger asChild>
									<div>
										<Switch.Root
											checked={module.enabled}
											disabled={module.required}
											onCheckedChange={() => onToggle(module.id)}
											className={
												'relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ' +
												(module.enabled
													? module.required
														? 'bg-green-300'
														: 'bg-green-500'
													: 'bg-gray-200')
											}
										>
											<span
												className={
													(module.enabled ? 'translate-x-6' : 'translate-x-1') +
													' inline-block w-4 h-4 transform bg-white rounded-full transition-transform'
												}
											/>
										</Switch.Root>
									</div>
								</Tooltip.Trigger>
							)}
							{module.required && (
								<Tooltip.Content
									side="top"
									align="center"
									className="bg-gray-800 text-white text-xs rounded px-2 py-1"
								>
									Module obligatoire, impossible de le désactiver
									<Tooltip.Arrow className="fill-current text-gray-800" />
								</Tooltip.Content>
							)}
							{!module.required && (
								<Switch.Root
									checked={module.enabled}
									disabled={module.required}
									onCheckedChange={() => onToggle(module.id)}
									className={
										'relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400 ' +
										(module.enabled ? 'bg-green-500' : 'bg-gray-200')
									}
								>
									<span
										className={
											(module.enabled ? 'translate-x-6' : 'translate-x-1') +
											' inline-block w-4 h-4 transform bg-white rounded-full transition-transform'
										}
									/>
								</Switch.Root>
							)}
						</Tooltip.Root>
					</Tooltip.Provider>

					{/* Configure button if configurable */}
					{configurable && (
						<button
							onClick={handleSettingsClick}
							className="text-gray-500 hover:text-gray-700 transition-colors"
							aria-label={`Paramétrer ${module.title}`}
						>
							<FontAwesomeIcon icon={faCog} className="w-5 h-5" />
						</button>
					)}
				</div>
			</div>
		</div>
	);
};

export default ModuleCard;
